import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  IonButtons,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { Listener } from 'history';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppHeader_v2 as AppHeader,
  Container_v2 as Container,
  OnBoardingChartScreenPreviewFeature,
  OnBoardingChartScreenType,
  OnBoardingCompareScreenPreviewFeature,
  OnBoardingCompareScreenType,
  OnBoardingConfirmScreenPreviewFeature,
  OnBoardingConfirmScreenType,
  OnBoardingEmailScreenPreviewFeature,
  OnBoardingEmailScreenType,
  OnBoardingGraphScreenPreviewFeature,
  OnBoardingGraphScreenType,
  OnBoardingInfoScreenPreviewFeature,
  OnBoardingInfoScreenType,
  OnBoardingLoaderScreenPreviewFeature,
  OnBoardingLoaderScreenType,
  OnBoardingMultiSelectionScreenPreviewFeature,
  OnBoardingMultiSelectScreenType,
  OnBoardingPaywallScreenPreviewFeature,
  OnBoardingPaywallScreenType,
  OnBoardingReviewsScreenType,
  OnBoardingScreenOptions,
  OnBoardingScreenOptionWithScaleType,
  OnBoardingSingleSelectionScreenPreviewFeature,
  OnBoardingSingleSelectScreenType,
  OnBoardingStartScreenPreviewFeature,
  OnBoardingStartScreenType,
  Specialist,
  SpecialistReview,
  Button_v2 as Button,
  OnboardingVariant,
  LocaleCurrencyMapper,
  Tariff,
  OnBoardingGoalSelectionScreenPreviewFeature,
  OnBoardingGoalSelectScreenType,
  OnboardingProgressTemplate,
} from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import {
  FunnelTypeEnum,
  useAnalytics,
  useAuthFlow,
  useOnBoardingFlowAction,
  useLog,
  validateEmail,
} from '../../../../shared';
import {
  flowIsTestingSelector,
  flowSelectedAnswersByScreenSelector,
  flowSelectedAnswersSelector,
  flowUseAdditionalPromotionalCodeDiscountSelector,
  flowUserEmailSelector,
  getGoalScreenDataSelector,
  getLocalizedAreasOfWorkByClientProblemSelector,
  onBoardingFlowSelector,
} from '../../../../redux/onBoardingFlow/selector';
import { FlowScreenPageStyled } from './FlowScreen.style';
import {
  setAnswer,
  setCurrentScreenId,
  setErrorMessage,
  setGoalScreenData,
  setProgress,
  setUseAdditionalPromotionalCodeDiscount,
  setUserEmail,
  updateScreensHistory,
} from '../../../../redux/onBoardingFlow/reducer';
import { OnBoardingFlowPageFooter } from '../../components';
import { getOnBoardingReviews } from '../../../../api/onboarding';
import { TranslatedOnBoardingReviewsScreenPreviewFeature } from '../../../../translated-components';
import { userSelector } from 'src/redux/user/selector';
import { closeOutline } from 'ionicons/icons';
import { createBalanceByTariff } from 'src/api/trial-session';
import { CheckoutData } from 'src/shared/types/checkout';
import { PaymentCheckoutModalFeature } from 'src/features/PaymentCheckoutModalFeature';
import AdditionalCheckoutPromocodeModalFeature from 'src/features/AdditionalCheckoutPromocodeModalFeature';
import { environment } from 'src/enviroments';
import { useFlowPromocode } from 'src/shared/hooks/useFlowPromocode';
import { overrideTFn } from 'src/shared/utils/temp';
import { getReviewsByIds } from 'src/api/reviews';
import { getSpecialistsByIDs } from 'src/api/specialist/specialist';
import { ANIMATION_DURATION } from 'src/shared/utils/pageAnimation';
import { SupportedLangsType } from 'src/shared/enums/supportedLangs.enum';
import { globalVariables } from 'src/redux/firestoreGlobalVariables/selector';
import classNames from 'classnames';
import { PromoCodeTimerFeature } from 'src/features/PromoCodeTimerFeature';
import useToast from 'src/context/ToastContext/ToastContext';
import { StripeExpressCheckoutElementClickEvent } from '@stripe/stripe-js';
import { Capacitor } from '@capacitor/core';
import { ILocalizedAreasOfWorkByClientProblem } from 'src/pages/types';
import { animateGoalScreen } from '../../animateGoalScreen';

const REVIEWS_LIMIT = 20;

const FlowScreenPage: FC<RouteComponentProps<{ screenId?: string; flowId: string }>> = ({ match }) => {
  const [, , flowId] = match.path.split('/');
  const { screenId } = match.params;

  const { currentScreenData, getNextURL, currentBgColor, buttonsStyles, subgoalButtonsStyles } =
    useOnBoardingFlowAction(screenId);
  const flowData = useSelector(onBoardingFlowSelector);
  const selectedAnswers = useSelector(flowSelectedAnswersByScreenSelector(screenId ?? currentScreenData?.id ?? ''));
  const selectedAllAnswers = useSelector(flowSelectedAnswersSelector);
  const isTestingModeSelector = useSelector(flowIsTestingSelector);
  const localizedAreasOfWorkByClientProblem = useSelector(getLocalizedAreasOfWorkByClientProblemSelector);
  const currentUser = useSelector(userSelector);

  const [answers, setAnswers] = useState<string[] | undefined>(selectedAnswers);
  const [isPageView, setIsPageView] = useState(false);
  const [isPagePresented, setIsPagePresented] = useState(false);
  const [reviewsState, setReviewsState] = useState<{
    reviews: SpecialistReview[];
    isLoading: boolean;
    offset: number;
    isEnd: boolean;
  }>({
    reviews: [],
    isLoading: false,
    offset: 0,
    isEnd: false,
  });
  const history = useHistory();

  const navigation = useIonRouter();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(['ui', 'checkout']);
  const { language } = i18n;
  const { trackPredefinedEvent } = useAnalytics();

  const isNextClicked = useRef(false);
  const userEmail = useSelector(flowUserEmailSelector);
  const [emailErrorText, setEmailErrorText] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { logError } = useLog();
  const { triggerLinkWithEmailAndPasswordFlow, isEmailExist, authUser } = useAuthFlow();
  const [paymentCheckoutModalIsOpen, setPaymentCheckoutModalIsOpen] = useState(false);
  const checkoutData = useRef<CheckoutData | null>(null);
  const selectedTariffData = useRef<Tariff | null>(null);
  const [isAdditionalCheckoutPromocodeModalOpen, setIsAdditionalCheckoutPromocodeModalOpen] = useState(false);
  const useAdditionalPromotionalCodeDiscount = useSelector(flowUseAdditionalPromotionalCodeDiscountSelector);
  const { currentGoalScreenCount } = useSelector(getGoalScreenDataSelector);
  const { promocodeEndDate, promocodePercent } = useFlowPromocode();
  const [reviews, setReviews] = useState<SpecialistReview[]>([]);
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const isAdditionalDataLoading = useRef(false);
  const globalVars = useSelector(globalVariables);
  const ref = useRef<HTMLIonContentElement>(null);
  const { showToast } = useToast();
  const isScrollHalfPage = useRef(false);
  const isScrollFullPage = useRef(false);
  const [goalAnswers, setGoalAnswers] = useState<Record<string, string[] | null>>({});

  const isSubGoalScreen = useMemo(() => Boolean(currentGoalScreenCount), [currentGoalScreenCount]);

  const localizedAreasOfWorkByClientProblemCategoriesMap = useMemo(() => {
    return localizedAreasOfWorkByClientProblem.reduce(
      (acc, c) => {
        acc[c.id] = c;
        return acc;
      },
      {} as Record<string, ILocalizedAreasOfWorkByClientProblem>
    );
  }, [localizedAreasOfWorkByClientProblem]);

  const localizedAreasOfWorkByClientProblemSymptomsMap = useMemo(() => {
    return localizedAreasOfWorkByClientProblem
      .flatMap((c) => c.items ?? [])
      .reduce(
        (acc, s) => {
          acc[s.id] = s;
          return acc;
        },
        {} as Record<string, { id: string; label: string; order: number; parentId?: string }>
      );
  }, [localizedAreasOfWorkByClientProblem]);

  const filteredGoalAnswers = useMemo(() => {
    return Object.keys(goalAnswers).filter((key) => Array.isArray(goalAnswers[key]));
  }, [goalAnswers]);

  const currentGoalCategoryId = useMemo(
    () => filteredGoalAnswers[currentGoalScreenCount - 1],
    [filteredGoalAnswers, currentGoalScreenCount]
  );

  const updateGoalScreenData = () => {
    const nextGoalScreenCount = currentGoalScreenCount + 1;

    dispatch(
      setProgress({
        screenId: screenId ?? currentScreenData?.id ?? '',
        currentGoalScreenCount: nextGoalScreenCount,
      })
    );

    dispatch(
      setGoalScreenData({
        currentGoalScreenCount: nextGoalScreenCount,
      })
    );

    dispatch(
      setAnswer({
        screenId: screenId ?? currentScreenData?.id ?? '',
        answerIds: [
          ...Object.values(goalAnswers).flatMap((goalAnswersIds) => goalAnswersIds ?? []),
          ...Object.keys(goalAnswers),
        ],
      })
    );
  };

  const getPrevGoalAnswers = (goalSelectedAnswers: string[]) => {
    return goalSelectedAnswers.reduce<Record<string, string[] | null>>((acc, answer) => {
      const parentId = localizedAreasOfWorkByClientProblemSymptomsMap[answer]?.parentId;
      const isOther = answer.endsWith('-other');

      if (isOther) {
        const [categoryId] = answer.split('-');

        acc[categoryId] = [...(acc[categoryId] ?? []), answer];
      } else if (localizedAreasOfWorkByClientProblemCategoriesMap[answer] && !acc[answer]) {
        acc[answer] = localizedAreasOfWorkByClientProblemCategoriesMap[answer].items?.length ? [] : null;
      } else if (parentId) {
        (acc[parentId] ??= []).push(answer);
      }

      return acc;
    }, {});
  };

  useIonViewDidEnter(() => {
    setIsPageView(true);
    setIsPagePresented(true);
    isNextClicked.current = false;
    setAnswers(selectedAnswers);

    const goalScreen = Object.entries(flowData?.screens ?? {}).find(
      ([, screen]) => screen.type === OnBoardingScreenOptions.GOAL
    );

    if (goalScreen?.[0] && selectedAllAnswers[goalScreen[0]]) {
      const prevGoalAnswers = getPrevGoalAnswers(selectedAllAnswers[goalScreen[0]]);

      setGoalAnswers(prevGoalAnswers);
    }

    console.info(`[FLOW]: Enter to flowId(${flowId}) screenId: ${screenId ?? currentScreenData?.id ?? ''}`);

    if (!screenId && !currentScreenData?.id) {
      console.error(`[FLOW]: Screen id not found in flowId ${flowId}`);
      return;
    }
  }, [
    screenId,
    selectedAnswers,
    selectedAllAnswers,
    currentScreenData?.id,
    currentScreenData?.type,
    currentGoalScreenCount,
    flowData?.screens,
    getPrevGoalAnswers,
  ]);

  useIonViewDidLeave(() => {
    setIsPageView(false);
    isNextClicked.current = false;
    console.info(`[FLOW]: Leave flowId(${flowId}) screenId: ${screenId ?? currentScreenData?.id ?? ''}`);
  });

  useEffect(() => {
    if (!isPageView) return;
    if (!screenId || !flowData) return;

    dispatch(setCurrentScreenId(screenId));
    dispatch(setProgress({ screenId }));
    dispatch(updateScreensHistory(screenId));
  }, [isPageView, screenId, flowData]);

  const commonEventData = useMemo(() => {
    return {
      funnel_id: flowId,
      funnel_name: flowData?.name,
      funnel_type: flowData?.isDefault
        ? FunnelTypeEnum.DEFAULT
        : flowData?.isWebToWeb
          ? FunnelTypeEnum.WEB_TO_WEB
          : FunnelTypeEnum.WEB_TO_APP,
      screen_id: screenId ?? currentScreenData?.id ?? '',
    };
  }, [flowId, flowData, screenId, currentScreenData]);

  const getTrackPredefinedEventAnswers = (answersData?: string[]) => {
    if (!currentScreenData || !answersData) return [];

    const { type } = currentScreenData;

    if ('options' in currentScreenData) {
      return answersData
        .map((answerId) => {
          const option = currentScreenData.options?.find((o) => o.id === answerId);
          return option ? { id: option.id, question: '', answer: option.label } : null;
        })
        .filter(Boolean) as { id: string; question?: string; answer: string }[];
    }

    if (type === OnBoardingScreenOptions.LIST_LOADER) {
      return answersData.map((answer, index) => ({
        question: (currentScreenData as OnBoardingLoaderScreenType).modals?.[index]?.label ?? '',
        id: '',
        answer,
      }));
    }

    return [];
  };

  const getNextURLOnce = async (answersData?: string[], forceRedirect: boolean = false) => {
    if (!isPageView) return;
    if (isNextClicked.current) return;

    if (
      !isTestingModeSelector &&
      currentScreenData &&
      answersData &&
      'options' in currentScreenData &&
      currentScreenData.type !== OnBoardingScreenOptions.GOAL
    ) {
      trackPredefinedEvent('FUNNEL_ANSWERS', {
        ...commonEventData,
        screen_name: currentScreenData?.name ?? '',
        screen_order: currentScreenData?.order ?? 0,
        answers: getTrackPredefinedEventAnswers(answersData),
      });
    } else if (currentScreenData && currentScreenData.type === OnBoardingScreenOptions.LIST_LOADER && answersData) {
      trackPredefinedEvent('FUNNEL_ANSWERS', {
        ...commonEventData,
        screen_name: currentScreenData?.name ?? '',
        screen_order: currentScreenData?.order ?? 0,
        answers: getTrackPredefinedEventAnswers(answersData),
      });
    } else if (
      currentScreenData &&
      ![
        OnBoardingScreenOptions.START,
        OnBoardingScreenOptions.EMAIL,
        OnBoardingScreenOptions.CONFIRM,
        OnBoardingScreenOptions.PAYWALL,
        OnBoardingScreenOptions.GOAL,
      ].includes(currentScreenData.type)
    ) {
      trackPredefinedEvent('FUNNEL_CONTINUE_CLICKED', {
        ...commonEventData,
        screen_name: currentScreenData?.name ?? '',
        screen_order: currentScreenData?.order ?? 0,
      });
    }

    isNextClicked.current = true;
    navigation.routeInfo.routeDirection = 'forward';
    navigation.push(getNextURL(answersData, forceRedirect), 'forward', 'push');
    setAnswers(undefined);
  };

  useEffect(() => {
    if (!currentScreenData || currentScreenData?.type !== OnBoardingScreenOptions.REVIEWS) return;
    if (reviewsState.isLoading || reviewsState.reviews.length) return;
    fetchReviews();
  }, [isPageView, currentScreenData, reviewsState]);

  // TODO concider to use useIonViewWillEnter with useIonViewWillLeave
  useEffect(() => {
    if (
      !currentScreenData?.type ||
      ![OnBoardingScreenOptions.CONFIRM, OnBoardingScreenOptions.PAYWALL, OnBoardingScreenOptions.EMAIL].includes(
        currentScreenData?.type
      )
    )
      return;
    if (authUser?.email) {
      setIsPageView(true);
      setTimeout(() => {
        getNextURLOnce(undefined, true);
      }, ANIMATION_DURATION + 100);
      return;
    }
    const currentUrl = history.location.pathname;
    const historyListener: Listener = ({ action }) => {
      if (action === 'POP') {
        navigation.push(currentUrl, 'none');
      }
    };
    const unlisten = history.listen(historyListener);
    return () => {
      unlisten();
    };
  }, [currentScreenData?.type, history, navigation, authUser?.email]);

  useEffect(() => {
    (async () => {
      const reviewsIds = Object.values(flowData?.screens ?? {}).flatMap((s) => ('reviewsIds' in s ? s.reviewsIds : []));

      if (reviewsIds.length) {
        const reviewsResponse = await getReviewsByIds(reviewsIds as string[]);

        setReviews(reviewsResponse);
      }
    })();
  }, [flowData?.screens]);

  useEffect(() => {
    if (
      !currentScreenData ||
      currentScreenData?.type !== OnBoardingScreenOptions.PAYWALL ||
      isAdditionalDataLoading.current
    )
      return;
    (async () => {
      try {
        isAdditionalDataLoading.current = true;
        const specialistsResponse = await getSpecialistsByIDs(
          (currentScreenData as OnBoardingPaywallScreenType).specialistsIds ?? []
        );

        setSpecialists(specialistsResponse);
      } catch (e) {
        console.error(e);
      } finally {
        isAdditionalDataLoading.current = false;
      }
    })();
  }, [currentScreenData]);

  const fetchReviews = async () => {
    if (reviewsState.isLoading) return;
    try {
      setReviewsState((prevState) => ({ ...prevState, isLoading: true }));
      const res = await getOnBoardingReviews(REVIEWS_LIMIT, reviewsState.offset, language);
      if (res) {
        setReviewsState((prevState) => ({
          ...prevState,
          reviews: [...prevState.reviews, ...res],
          offset: prevState.offset + REVIEWS_LIMIT,
          isEnd: res.length < REVIEWS_LIMIT,
        }));
      }
    } catch (e: any) {
      dispatch(setErrorMessage(e?.message || 'Something went wrong'));
    } finally {
      setReviewsState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const handleProgressEnd = () => {
    getNextURLOnce(answers);
  };

  const [loaderScreenKey, setLoaderScreenKey] = useState(0);
  useIonViewWillEnter(() => {
    if (
      currentScreenData?.type === OnBoardingScreenOptions.LOADER ||
      currentScreenData?.type === OnBoardingScreenOptions.LIST_LOADER
    ) {
      isNextClicked.current = false;
      setIsPageView(true);
      setLoaderScreenKey((prev) => prev + 1);
    }
  }, [currentScreenData?.type]);

  const handleChangeEmail = (email: string) => {
    dispatch(setUserEmail(email));
    setEmailErrorText('');
  };

  const handleChangePassword = (value: string) => {
    setPassword(value);
  };

  const isDisabled = useMemo(() => {
    switch (currentScreenData?.type) {
      case OnBoardingScreenOptions.MULTI_SELECT:
        return !answers?.length;
      case OnBoardingScreenOptions.GOAL:
        if (!isSubGoalScreen) {
          return !Object.keys(goalAnswers).length;
        } else {
          return !goalAnswers[currentGoalCategoryId]?.length;
        }
      case OnBoardingScreenOptions.EMAIL:
        return !validateEmail(userEmail ?? '');
      case OnBoardingScreenOptions.CONFIRM:
        return !validateEmail(userEmail ?? '') || password.length <= 5;
      default:
        return false;
    }
  }, [currentScreenData?.type, answers, userEmail, password, goalAnswers, isSubGoalScreen, currentGoalCategoryId]);

  const trackGoalAnswers = () => {
    trackPredefinedEvent('FUNNEL_ANSWERS', {
      ...commonEventData,
      screen_name: currentScreenData?.name ?? '',
      screen_order: currentScreenData?.order ?? 0,
      answers: isSubGoalScreen
        ? ((goalAnswers[currentGoalCategoryId] ?? [])
            .map((answerId) => {
              const isOther = answerId.endsWith('-other');
              const [categoryId] = answerId.split('-');

              if (isOther && categoryId !== currentGoalCategoryId) {
                return null;
              }

              return {
                id: answerId,
                question: '',
                answer: isOther
                  ? t('goal_screen.other_symptom_label')
                  : localizedAreasOfWorkByClientProblemSymptomsMap[answerId]?.label || '',
              };
            })
            .filter(Boolean) as { id: string; question?: string; answer: string }[])
        : Object.keys(goalAnswers)?.map((answerId) => {
            return {
              id: answerId,
              question: '',
              answer: localizedAreasOfWorkByClientProblemCategoriesMap[answerId]?.name || '',
            };
          }),
    });
  };

  const handleNextClick = async () => {
    if (isDisabled) return;
    switch (currentScreenData?.type) {
      case OnBoardingScreenOptions.EMAIL:
      case OnBoardingScreenOptions.CONFIRM: {
        if (!userEmail) {
          return setEmailErrorText(t('flow.validation.email.required'));
        }
        setIsLoading(true);
        try {
          const isExist = await isEmailExist(userEmail);
          let verificationMessage = 'Success';
          let hasError = false;
          if (isExist) {
            verificationMessage = 'Email exists';
            setEmailErrorText(t('flow.validation.email.exist'));
            hasError = true;
          }
          if (currentScreenData?.type === OnBoardingScreenOptions.CONFIRM) {
            await triggerLinkWithEmailAndPasswordFlow({
              email: userEmail,
              password,
            });
          }

          if (currentScreenData?.type === OnBoardingScreenOptions.EMAIL) {
            trackPredefinedEvent('FUNNEL_REGISTRATION_EMAIL_CONTINUE_CLICKED', {
              ...commonEventData,
              user_email: userEmail ?? '',
              verification_message: verificationMessage,
            });
          } else if (currentScreenData?.type === OnBoardingScreenOptions.CONFIRM) {
            trackPredefinedEvent('FUNNEL_REGISTRATION_CONFIRMATION_CONTINUE_CLICKED', {
              ...commonEventData,
              user_email: userEmail ?? '',
              verification_message: verificationMessage,
            });
          }

          if (!hasError) {
            getNextURLOnce(answers);
          }
        } catch (e) {
          logError(e);
        } finally {
          setIsLoading(false);
        }
        break;
      }
      case OnBoardingScreenOptions.GOAL: {
        trackGoalAnswers();

        if (currentGoalScreenCount < Object.keys(filteredGoalAnswers).length) {
          animateGoalScreen({
            updateGoalScreenData,
            direction: 'forward',
          });
        } else {
          getNextURLOnce([
            ...Object.values(goalAnswers).flatMap((goalAnswersIds) => goalAnswersIds ?? []),
            ...Object.keys(goalAnswers),
          ]);
        }
        break;
      }
      default:
        getNextURLOnce(answers);
    }
  };

  const isCloseButtonVisible =
    !!currentUser?.id &&
    currentUser?.is_viewed_specialist_list &&
    currentScreenData?.type === OnBoardingScreenOptions.START;

  const isShowFooter = useMemo(() => {
    if (!currentScreenData) return false;
    return ![OnBoardingScreenOptions.PAYWALL].includes(currentScreenData.type);
  }, [currentScreenData?.type]);

  const handleBuyTariff = async (tariffId: string) => {
    try {
      if (!userEmail) {
        return setEmailErrorText(t('flow.validation.email.required'));
      }
      setIsLoading(true);

      selectedTariffData.current =
        (currentScreenData as OnBoardingPaywallScreenType).tariffs.find((tariff) => tariff.id === tariffId) ?? null;
      const res = await createBalanceByTariff({
        selectedPriceId: tariffId,
        clientEmail: userEmail,
        onboardingKey: flowId,
        useAdditionalPromotionalCodeDiscount,
      });
      checkoutData.current = res;
      setPaymentCheckoutModalIsOpen(true);
    } catch (error: any) {
      const errorMessage = Array.isArray(error?.error) ? error?.error?.[0]?.msg : null;
      if (errorMessage?.includes('client_has_balance_coupons')) {
        trackPredefinedEvent('FUNNEL_CHECKOUT_PAYMENT_RETRY_AFTER_PASSWORD', {
          ...commonEventData,
          user_email: userEmail ?? '',
        });
      }

      logError(error);
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  };

  const termsOfUseLink =
    globalVars?.terms_of_conditions?.[flowData?.locale as SupportedLangsType] ?? globalVars?.terms_of_conditions?.en;

  const dispatchBuyTariffEvent = () => {
    trackPredefinedEvent('FUNNEL_PAYWALL_GET_MY_SESSION_CLICKED', {
      ...commonEventData,
      user_email: userEmail ?? '',
    });
    window.dispatchEvent(new CustomEvent('to-tariff-buy'));
  };

  const handlChangeScaleSelect = (answer: string) => {
    if (!currentScreenData || !('options' in currentScreenData)) return;

    const hasFeedbackInfo = currentScreenData.options?.some((option) => {
      const opt = option as OnBoardingScreenOptionWithScaleType;
      return !!opt.feedbackTitle && !!opt.feedbackDescription;
    });

    if (hasFeedbackInfo) {
      setAnswers([answer]);
    } else {
      getNextURLOnce([answer]);
    }
  };

  useEffect(() => {
    const ionContentEl = ref.current;

    if (currentScreenData?.type !== OnBoardingScreenOptions.PAYWALL) return;
    const handleScroll = async () => {
      if (!ionContentEl) return;

      const scrollEl = await ionContentEl.getScrollElement();
      const scrollTop = scrollEl.scrollTop;
      const scrollHeight = scrollEl.scrollHeight;
      const clientHeight = scrollEl.clientHeight;

      const scrollPercent = Math.round(((scrollTop + clientHeight) / scrollHeight) * 100);

      if (scrollPercent >= 50 && !isScrollHalfPage.current) {
        isScrollHalfPage.current = true;
        trackPredefinedEvent('FUNNEL_PAYWALL_SCROLL_50_PERCENT', {
          ...commonEventData,
          user_email: userEmail ?? '',
        });
      }

      if (scrollPercent >= 100 && !isScrollFullPage.current) {
        isScrollFullPage.current = true;
        trackPredefinedEvent('FUNNEL_PAYWALL_SCROLL_100_PERCENT', {
          ...commonEventData,
          user_email: userEmail ?? '',
        });
      }
    };

    ionContentEl?.addEventListener('ionScroll', handleScroll);

    return () => {
      isScrollHalfPage.current = false;
      isScrollFullPage.current = false;
      ionContentEl?.removeEventListener('ionScroll', handleScroll);
    };
  }, [trackPredefinedEvent, currentScreenData?.type]);

  const handleTrackTariffEvent = (
    tariffData: OnBoardingPaywallScreenType['tariffs'][number],
    eventName: 'FUNNEL_PAYWALL_PLAN_INFO_VIEWED' | 'FUNNEL_PAYWALL_PURCHASE_BUTTON_CLICKED'
  ) => {
    const purchaseValue = useAdditionalPromotionalCodeDiscount
      ? tariffData.priceAfterAdditionalDiscount
      : tariffData.priceAfterDiscount;
    trackPredefinedEvent(eventName, {
      ...commonEventData,
      user_email: userEmail ?? '',
      purchase_value: tariffData.priceBase / 100,
      purchase_value_after_discount: purchaseValue / 100,
      purchase_currency: LocaleCurrencyMapper[flowData?.locale as keyof typeof LocaleCurrencyMapper],
      purchase_plan: tariffData.label,
    });
  };

  const onPaymentCheckoutModalDidPresent = () => {
    if (!checkoutData.current) return;

    trackPredefinedEvent('FUNNEL_CHECKOUT_SCREEN_VIEWED', {
      ...commonEventData,
      user_email: userEmail ?? '',
      purchase_value: checkoutData.current.amount_before_discount / 100,
      purchase_value_after_discount: checkoutData.current.amount_after_discount / 100,
      purchase_currency: LocaleCurrencyMapper[flowData?.locale as keyof typeof LocaleCurrencyMapper],
      purchase_plan: selectedTariffData.current?.label ?? '',
    });
  };

  const onPaymentCheckoutModalPayNowButtonClick = (event?: StripeExpressCheckoutElementClickEvent) => {
    if (!checkoutData.current) return;

    const paymentMethodMapper = {
      google_pay: 'Google Pay',
      apple_pay: 'Apple Pay',
      amazon_pay: 'Amazon Pay',
      link: 'Link',
      paypal: 'PayPal',
      klarna: 'Klarna',
    };

    trackPredefinedEvent('FUNNEL_CHECKOUT_PAY_NOW_BUTTON_CLICKED', {
      ...commonEventData,
      user_email: userEmail ?? '',
      purchase_value: checkoutData.current.amount_before_discount / 100,
      purchase_value_after_discount: checkoutData.current.amount_after_discount / 100,
      purchase_currency: LocaleCurrencyMapper[flowData?.locale as keyof typeof LocaleCurrencyMapper],
      purchase_plan: selectedTariffData.current?.label ?? '',
      payment_type: event?.expressPaymentType ? paymentMethodMapper[event.expressPaymentType] : 'Card',
    });
  };

  const onAdditionalCheckoutPromocodeModalDidPresent = () => {
    if (!checkoutData.current) return;

    trackPredefinedEvent('FUNNEL_PAYWALL_DISCOUNT_OFFERED', {
      ...commonEventData,
      user_email: userEmail ?? '',
      promocode_value: (currentScreenData as OnBoardingPaywallScreenType).additionalDiscount,
    });
  };

  const handleChangeGoalSelect = (changedGoalAnswers: string[]) => {
    if (!currentScreenData || !('options' in currentScreenData)) return;

    const newGoalAnswers = !isSubGoalScreen
      ? Object.fromEntries(
          changedGoalAnswers.map((answer) => [
            answer,
            goalAnswers[answer] ??
              (localizedAreasOfWorkByClientProblemCategoriesMap[answer]?.items?.length ? [] : null),
          ])
        )
      : { ...goalAnswers, [currentGoalCategoryId]: changedGoalAnswers };

    setGoalAnswers(newGoalAnswers);
  };

  const goalScreenTemplate = useMemo(() => {
    if (!currentScreenData) return;

    if (!isSubGoalScreen) {
      return 'isOptionView' in currentScreenData && currentScreenData.isOptionView ? 'icon' : 'checkbox';
    } else {
      return 'subgoalIsOptionView' in currentScreenData && currentScreenData.subgoalIsOptionView ? 'icon' : 'checkbox';
    }
  }, [currentScreenData, currentGoalScreenCount, isSubGoalScreen]);

  const areasOfWorkByClientProblemOptions = useMemo(() => {
    if (!currentScreenData) return [];

    if (currentGoalScreenCount > 0) {
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

      const currentCategory = localizedAreasOfWorkByClientProblemCategoriesMap[currentGoalCategoryId];

      if (!currentCategory?.items?.length) return [];

      return (
        [
          ...currentCategory.items,
          {
            id: `${currentCategory.id}-other`,
            label: t('goal_screen.other_symptom_label'),
            order: currentCategory.items.length + 2,
          },
        ].map((area) => ({
          id: area.id,
          alphabeticalOrder: letters[area.order],
          label: area.label,
          filtrationAnswerId: area.id,
        })) ?? []
      );
    } else {
      return (currentScreenData as OnBoardingGoalSelectScreenType).options ?? [];
    }
  }, [currentGoalScreenCount, localizedAreasOfWorkByClientProblem, currentGoalCategoryId, currentScreenData]);

  if (!currentScreenData) {
    return (
      <IonPage>
        <AppHeader />
      </IonPage>
    );
  }

  const SINGLE_SELECT_TEMPLATES: Partial<Record<OnBoardingScreenOptions, 'image' | 'roundImage'>> = {
    [OnBoardingScreenOptions.SINGLE_IMAGE_SELECT]: 'image',
    [OnBoardingScreenOptions.SINGLE_ROUND_IMAGE_SELECT]: 'roundImage',
  };

  const promocodeHeightClassName =
    flowData?.progress?.template === OnboardingProgressTemplate.counter ? '!h-[130px]' : '!h-[100px]';
  const headerHeightClassName =
    flowData?.progress?.template === OnboardingProgressTemplate.counter ? '!h-[65px] align-start' : '';

  const isProgressVisibleForCurrentScreen =
    typeof currentScreenData?.isProgressVisible === 'boolean' ? currentScreenData.isProgressVisible : true;
  const isCounterProgressTemplate = flowData?.progress?.template === OnboardingProgressTemplate.counter;

  const shouldApplyPromoHeight =
    currentScreenData?.type === OnBoardingScreenOptions.PAYWALL && isCounterProgressTemplate
      ? isProgressVisibleForCurrentScreen
      : currentScreenData?.type !== OnBoardingScreenOptions.PAYWALL;

  const onboardingTheme = {
    ...flowData?.theme_v2,
    template: flowData?.theme_v2?.template ?? flowData?.theme ?? OnboardingVariant.default,
  };

  return (
    <FlowScreenPageStyled color={currentBgColor}>
      <AppHeader>
        {isCloseButtonVisible ? (
          <IonButtons slot={'end'}>
            <IonIcon
              icon={closeOutline}
              style={{
                marginTop: '12px',
                fontSize: '27px',
                color: '#1D201F',
                cursor: 'pointer',
              }}
              onClick={() => {
                dispatch(setCurrentScreenId(null));
                navigation.goBack();
              }}
            />
          </IonButtons>
        ) : !!promocodeEndDate ? (
          <div
            className={classNames({
              [promocodeHeightClassName]: shouldApplyPromoHeight,
            })}
          >
            {currentScreenData?.type === OnBoardingScreenOptions.PAYWALL && (
              <PromoCodeTimerFeature
                endDate={promocodeEndDate}
                percentOff={promocodePercent}
                expandWidth
                endContent={
                  <Button
                    variant={onboardingTheme.template}
                    size="small"
                    fullWidth={false}
                    onClick={dispatchBuyTariffEvent}
                  >
                    {t?.('paywall.buy_session')}
                  </Button>
                }
              />
            )}
          </div>
        ) : (
          <div className={headerHeightClassName} />
        )}
      </AppHeader>
      {currentScreenData && (
        <Container ref={ref} scrollEvents>
          {currentScreenData.type === OnBoardingScreenOptions.START && (
            <OnBoardingStartScreenPreviewFeature {...(currentScreenData as OnBoardingStartScreenType)} />
          )}
          {currentScreenData.type === OnBoardingScreenOptions.MULTI_SELECT && (
            <OnBoardingMultiSelectionScreenPreviewFeature
              {...(currentScreenData as OnBoardingMultiSelectScreenType)}
              onChange={setAnswers}
              defaultValues={answers}
              theme={onboardingTheme} // TODO: Consider moving theme logic to UI context in ui-components, to avoid passing it as a prop to every screen.
              template={'isOptionView' in currentScreenData && currentScreenData.isOptionView ? 'icon' : 'checkbox'}
            />
          )}
          {currentScreenData.type === OnBoardingScreenOptions.GOAL && (
            <OnBoardingGoalSelectionScreenPreviewFeature
              {...(currentScreenData as OnBoardingGoalSelectScreenType)}
              onChange={handleChangeGoalSelect}
              defaultValues={(!isSubGoalScreen ? Object.keys(goalAnswers) : goalAnswers[currentGoalCategoryId]) ?? []}
              theme={onboardingTheme}
              currentSelectionScreenCount={currentGoalScreenCount}
              template={goalScreenTemplate}
              maxSelectionCount={
                !isSubGoalScreen && 'maxSelectionCount' in currentScreenData
                  ? (currentScreenData.maxSelectionCount as number)
                  : undefined
              }
              initialVisibleCount={
                !isSubGoalScreen && 'initialVisibleCount' in currentScreenData
                  ? (currentScreenData.initialVisibleCount as number)
                  : undefined
              }
              categoryLabel={
                localizedAreasOfWorkByClientProblem.find((category) => category.id === currentGoalCategoryId)?.name
              }
              options={areasOfWorkByClientProblemOptions}
              t={t}
            />
          )}
          {currentScreenData.type === OnBoardingScreenOptions.SINGLE_SELECT && (
            <OnBoardingSingleSelectionScreenPreviewFeature
              {...(currentScreenData as OnBoardingSingleSelectScreenType)}
              onChange={(answer) => getNextURLOnce([answer])}
              defaultValues={selectedAnswers}
              theme={onboardingTheme}
              template={'isOptionView' in currentScreenData && currentScreenData.isOptionView ? 'icon' : 'radio'}
            />
          )}
          {[OnBoardingScreenOptions.SINGLE_IMAGE_SELECT, OnBoardingScreenOptions.SINGLE_ROUND_IMAGE_SELECT].includes(
            currentScreenData.type
          ) && (
            <OnBoardingSingleSelectionScreenPreviewFeature
              {...(currentScreenData as OnBoardingSingleSelectScreenType)}
              onChange={(answer) => getNextURLOnce([answer])}
              defaultValues={selectedAnswers}
              theme={onboardingTheme}
              template={SINGLE_SELECT_TEMPLATES[currentScreenData.type]}
            />
          )}
          {currentScreenData.type === OnBoardingScreenOptions.SINGLE_SCALE_SELECT && (
            <OnBoardingSingleSelectionScreenPreviewFeature
              {...(currentScreenData as OnBoardingSingleSelectScreenType)}
              onChange={handlChangeScaleSelect}
              defaultValues={answers ?? selectedAnswers}
              theme={onboardingTheme}
              template="emoji"
            />
          )}
          {currentScreenData.type === OnBoardingScreenOptions.INFO && (
            <OnBoardingInfoScreenPreviewFeature
              {...(currentScreenData as OnBoardingInfoScreenType)}
              isPageView={isPagePresented}
            />
          )}
          {currentScreenData.type === OnBoardingScreenOptions.LOADER && (
            <OnBoardingLoaderScreenPreviewFeature
              {...(currentScreenData as OnBoardingLoaderScreenType)}
              selectedAnswers={selectedAllAnswers}
              allScreens={flowData?.screens}
              onProgressEnd={handleProgressEnd}
              // need to re-render the component when go back to the loader screen
              key={loaderScreenKey}
            />
          )}
          {currentScreenData.type === OnBoardingScreenOptions.LIST_LOADER && (
            <OnBoardingLoaderScreenPreviewFeature
              {...(currentScreenData as OnBoardingLoaderScreenType)}
              onProgressEnd={handleProgressEnd}
              // need to re-render the component when go back to the loader screen
              key={loaderScreenKey}
              template="list"
              defaultValues={answers}
              onChange={setAnswers}
              reviews={reviews.filter((review) =>
                'reviewsIds' in currentScreenData ? currentScreenData.reviewsIds?.includes(review.id) : false
              )}
              theme={onboardingTheme}
              t={t}
            />
          )}
          {currentScreenData.type === OnBoardingScreenOptions.REVIEWS && (
            <>
              <TranslatedOnBoardingReviewsScreenPreviewFeature
                {...(currentScreenData as OnBoardingReviewsScreenType)}
                reviews={reviewsState.reviews}
                isLoading={reviewsState.isLoading}
              />
              <IonInfiniteScroll
                onIonInfinite={async (ev) => {
                  await fetchReviews();
                  ev.target.complete();
                }}
                disabled={reviewsState.isLoading || reviewsState.isEnd}
              >
                <IonInfiniteScrollContent />
              </IonInfiniteScroll>
            </>
          )}
          {currentScreenData.type === OnBoardingScreenOptions.EMAIL && (
            <OnBoardingEmailScreenPreviewFeature
              {...(currentScreenData as OnBoardingEmailScreenType)}
              t={t}
              email={userEmail}
              onChangeEmail={handleChangeEmail}
              emailErrorText={emailErrorText}
              onSubmit={handleNextClick}
            />
          )}
          {currentScreenData.type === OnBoardingScreenOptions.CONFIRM && (
            <OnBoardingConfirmScreenPreviewFeature
              {...(currentScreenData as OnBoardingConfirmScreenType)}
              t={t}
              email={userEmail}
              onChangeEmail={handleChangeEmail}
              emailErrorText={emailErrorText}
              password={password}
              onChangePassword={handleChangePassword}
              onSubmit={handleNextClick}
            />
          )}

          {currentScreenData.type === OnBoardingScreenOptions.COMPARE && (
            <OnBoardingCompareScreenPreviewFeature
              {...(currentScreenData as OnBoardingCompareScreenType)}
              t={t}
              theme={onboardingTheme}
            />
          )}

          {currentScreenData.type === OnBoardingScreenOptions.CHART && (
            <OnBoardingChartScreenPreviewFeature
              {...(currentScreenData as OnBoardingChartScreenType)}
              t={t}
              locale={flowData?.locale ?? 'uk'}
              theme={onboardingTheme}
            />
          )}

          {currentScreenData.type === OnBoardingScreenOptions.PAYWALL && (
            <OnBoardingPaywallScreenPreviewFeature
              {...(currentScreenData as OnBoardingPaywallScreenType)}
              t={overrideTFn(t, i18n)}
              specialists={specialists}
              reviews={reviews.filter((review) =>
                'reviewsIds' in currentScreenData ? currentScreenData.reviewsIds?.includes(review.id) : false
              )}
              locale={flowData?.locale}
              theme={onboardingTheme}
              onBuyTariff={handleBuyTariff}
              useAdditionalDiscount={useAdditionalPromotionalCodeDiscount ?? false}
              isLoading={isLoading}
              termsOfUseLink={termsOfUseLink}
              onTrackTariffEvent={handleTrackTariffEvent}
            />
          )}

          {currentScreenData.type === OnBoardingScreenOptions.GRAPH && (
            <OnBoardingGraphScreenPreviewFeature
              {...(currentScreenData as OnBoardingGraphScreenType)}
              isPageView={isPagePresented}
              theme={onboardingTheme}
            />
          )}
        </Container>
      )}
      {isShowFooter && (
        <OnBoardingFlowPageFooter
          currentScreenData={currentScreenData}
          buttonsStyles={currentGoalScreenCount ? subgoalButtonsStyles : buttonsStyles}
          isSubGoalScreen={isSubGoalScreen}
          isDisabled={isDisabled}
          onNextClick={handleNextClick}
          isLoading={isLoading}
          onboardingTheme={onboardingTheme}
          flowId={flowId}
        />
      )}
      {paymentCheckoutModalIsOpen && checkoutData.current && userEmail && (
        <PaymentCheckoutModalFeature
          isOpen={paymentCheckoutModalIsOpen}
          chargeCurrency={checkoutData.current?.currency}
          chargeAmount={checkoutData.current.amount_after_discount}
          discountAmount={checkoutData.current.discount_amount}
          amountBeforeDiscount={checkoutData.current.amount_before_discount}
          clientSecret={checkoutData.current.client_secret}
          returnUrl={`${environment.MAIN_WEB_URL}${getNextURL(answers)}`}
          customerEmail={userEmail}
          onDidDismiss={() => {
            setPaymentCheckoutModalIsOpen(false);
            setIsAdditionalCheckoutPromocodeModalOpen(true);
          }}
          onSuccess={() => {
            if (!checkoutData.current) return;

            trackPredefinedEvent('Purchase', {
              contents: [{ content_id: checkoutData.current.coupon_document_id, content_type: 'product' }],
              eventInstanceId: checkoutData.current.coupon_document_id,
              value: checkoutData.current.amount_after_discount / 100,
              currency: checkoutData.current.currency,
              userId: currentUser?.id,
              email: userEmail,
            });
            trackPredefinedEvent('purchase', {
              currency: checkoutData.current.currency,
              value: checkoutData.current.amount_after_discount / 100,
              product_name: selectedTariffData.current?.label ?? '',
              product_id: selectedTariffData.current?.id ?? '',
              platform: Capacitor.getPlatform(),
            });

            setPaymentCheckoutModalIsOpen(false);
            navigation.push(getNextURL(answers), 'forward', 'push');
          }}
          onDidPresent={onPaymentCheckoutModalDidPresent}
          onPayNowButtonClick={onPaymentCheckoutModalPayNowButtonClick}
        />
      )}

      <AdditionalCheckoutPromocodeModalFeature
        isOpen={isAdditionalCheckoutPromocodeModalOpen && useAdditionalPromotionalCodeDiscount === null}
        onClose={() => {
          setIsAdditionalCheckoutPromocodeModalOpen(false);
        }}
        onDidPresent={onAdditionalCheckoutPromocodeModalDidPresent}
        onGetDiscountClick={() => {
          trackPredefinedEvent('FUNNEL_PAYWALL_GET_DISCOUNT_BUTTON_CLICKED', {
            ...commonEventData,
            user_email: userEmail ?? '',
            promocode_value: (currentScreenData as OnBoardingPaywallScreenType).additionalDiscount,
          });
          dispatch(setUseAdditionalPromotionalCodeDiscount(true));
          setIsAdditionalCheckoutPromocodeModalOpen(false);
          if (ref.current) {
            ref.current.scrollToTop();
          }
        }}
        discount={(currentScreenData as OnBoardingPaywallScreenType)?.additionalDiscount ?? 0}
      />
    </FlowScreenPageStyled>
  );
};

export default memo(FlowScreenPage);
